import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, Mail, MessageSquare, ChevronLeft, Clock, WifiOff } from "lucide-react";
import { EmailDraftEditor, type FailedDraft } from "./email-draft-editor";

// Friendly labels for the recordFailedSend `sourceType` field. Anything not
// matched here falls through to the raw context label, then "Automated send".
const SOURCE_LABELS: Record<string, string> = {
  reminder: "Event reminder",
  drip: "Drip campaign",
  drip_campaign: "Drip campaign",
  scheduled_email: "Scheduled email",
  confirmation: "Booking confirmation",
  follow_up: "Follow-up",
  followup: "Follow-up",
  billing_statement: "Billing statement",
  outbox_retry: "Outbox retry",
  email: "Manual email",
};

function relTime(iso: string): string {
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "";
  const diff = d - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return diff >= 0 ? "any moment" : "just now";
  if (mins < 60) return diff >= 0 ? `in ${mins} min` : `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return diff >= 0 ? `in ${hrs}h` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return diff >= 0 ? `in ${days}d` : `${days}d ago`;
}

interface Props {
  initialDraftId?: string | null;
  onClearInitialDraft?: () => void;
}

export function EmailDraftsTab({ initialDraftId, onClearInitialDraft }: Props) {
  const [selected, setSelected] = useState<string | null>(initialDraftId ?? null);

  useEffect(() => {
    if (initialDraftId) setSelected(initialDraftId);
  }, [initialDraftId]);

  const { data: drafts, isLoading } = useQuery<FailedDraft[]>({
    queryKey: ["/api/email-drafts"],
  });

  const back = () => {
    setSelected(null);
    onClearInitialDraft?.();
  };

  if (selected) {
    return (
      <div className="space-y-3" data-testid="panel-draft-editor">
        <Button variant="ghost" size="sm" onClick={back} data-testid="button-back-to-drafts">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to drafts
        </Button>
        <EmailDraftEditor
          draftId={selected}
          onSent={back}
          onDiscarded={back}
          onCancel={back}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="status-loading-drafts">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!drafts || drafts.length === 0) {
    return (
      <Card data-testid="empty-drafts">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Mail className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">Outbox is empty</p>
          <p className="text-sm mt-1">
            When an automated email fails to send (Gmail disconnected, transient
            API error, or an unfilled merge token), it lands here so you can fix
            and re-send it. Pending sends retry automatically every hour during
            business hours.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="list-email-drafts">
      <p className="text-xs text-muted-foreground px-1">
        Failed sends retry automatically every hour during your office hours. Send
        Now or Discard from any row to clear it.
      </p>
      {drafts.map((d) => {
        const Icon = d.channel === "sms" ? MessageSquare : Mail;
        const recipient = d.recipientEmail || d.recipientPhone || "(no recipient)";
        const isPlaceholder = d.blockCode === "UNRENDERED_PLACEHOLDER";
        const sourceLabel =
          (d.sourceType && SOURCE_LABELS[d.sourceType]) ||
          d.contextLabel ||
          "Automated send";
        // Status badge: reconnect > placeholder > permanent > retrying > sent
        let statusBadge: JSX.Element;
        if (d.needsReconnect) {
          statusBadge = (
            <Badge variant="destructive" className="shrink-0" data-testid={`badge-draft-status-${d.id}`}>
              <WifiOff className="w-3 h-3 mr-1" />
              Gmail disconnected
            </Badge>
          );
        } else if (isPlaceholder) {
          statusBadge = (
            <Badge variant="destructive" className="shrink-0" data-testid={`badge-draft-status-${d.id}`}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              Needs your edit
            </Badge>
          );
        } else if (d.status === "failed_permanent") {
          statusBadge = (
            <Badge variant="destructive" className="shrink-0" data-testid={`badge-draft-status-${d.id}`}>
              <AlertTriangle className="w-3 h-3 mr-1" />
              Stopped retrying
            </Badge>
          );
        } else if (d.status === "sent") {
          statusBadge = (
            <Badge variant="secondary" className="shrink-0" data-testid={`badge-draft-status-${d.id}`}>
              Sent
            </Badge>
          );
        } else {
          // pending — show next retry hint.
          statusBadge = (
            <Badge variant="outline" className="shrink-0" data-testid={`badge-draft-status-${d.id}`}>
              <Clock className="w-3 h-3 mr-1" />
              {d.nextAttemptAt ? `Retry ${relTime(d.nextAttemptAt)}` : "Queued"}
            </Badge>
          );
        }
        return (
          <Card
            key={d.id}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setSelected(d.id)}
            data-testid={`row-draft-${d.id}`}
          >
            <CardContent className="py-3 flex items-center gap-3">
              <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate" data-testid={`text-draft-subject-${d.id}`}>
                    {d.subject || (d.channel === "sms" ? d.body.slice(0, 60) : "(no subject)")}
                  </span>
                  {statusBadge}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {sourceLabel} · To: {recipient}
                  {typeof d.attemptCount === "number" && d.attemptCount > 0
                    ? ` · ${d.attemptCount} ${d.attemptCount === 1 ? "attempt" : "attempts"}`
                    : ""}
                  {d.status === "sent" && d.lastAttemptAt
                    ? ` · sent ${new Date(d.lastAttemptAt).toLocaleString()}`
                    : <>{" · queued "}{relTime(d.createdAt)}</>}
                </div>
                {d.lastError && !isPlaceholder ? (
                  <div className="text-xs text-destructive truncate mt-0.5" data-testid={`text-draft-error-${d.id}`}>
                    {d.lastError}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
